import React, { useState, useEffect } from 'react';
import { Box, Button, Card, Grid, TextField, Typography } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import { NavLink, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import GiftAccordian from '../../layout/GiftAccordian';
import emailImg from '../../../assets/images/emailImage.png';
import graph from '../../../assets/images/graph.png';
import send from '../../../assets/images/send.png';
import giftCardAnimation from '../../../assets/images/giftCardAnimation.png';
import giftAnimationMedium from '../../../assets/images/giftAnimationMedium.png';
import useClasses from '../../../hooks/legacy/useClasses';
import styles from './GiftCards.style';
import { routeNames } from '../../../util/Constants';
import { useUser } from '../../../hooks/useUser';
import { redeemGCRequest } from '../../../redux/legacy/reducers/ducks/legacy/GiftDuck';
import {
  toggleSignIn,
  toggleSignUp,
} from '../../../redux/legacy/reducers/ducks/legacy/MainDuck';
import SignInForm from '../../input/SignInForm';
import SignUpForm from '../../input/SignUpForm';
import ForgotPassword from '../../layout/ForgotPassword';

function GiftCards() {
  const classes = useClasses(styles);
  const dispatch = useDispatch();
  const location = useLocation();
  const { isLoggedIn } = useUser();
  const [gcCodeString, setGCCodeString] = useState('');
  const [signIn, setSignIn] = useState(true);

  const { isSignInOpen, isSignUpOpen } = useSelector(({ main }) => ({
    isSignInOpen: main.isSignInOpen,
    isSignUpOpen: main.isSignUpOpen,
  }));

  // Extract code from URL parameter on component mount
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const codeFromUrl = searchParams.get('code');
    if (codeFromUrl) {
      setGCCodeString(codeFromUrl);
    }
  }, [location.search]);

  const handleChangeGCCode = (value) => {
    setGCCodeString(value);
  };

  const handleRedeem = () => {
    if (gcCodeString) {
      dispatch(redeemGCRequest(gcCodeString));
    }
    setGCCodeString('');
  };

  const handleSignInClick = () => {
    dispatch(toggleSignIn(true));
  };

  const toggleSignInModal = () => dispatch(toggleSignIn());
  const toggleSignUpModal = () => dispatch(toggleSignUp());

  useEffect(() => setSignIn(true), [isSignInOpen]);

  return (
    <Box className={classes.mainParentBox}>
      <Box className={classes.upperBox}>
        <Box className={classes.giftCardBox}>
          <Box className={classes.giftCardSubBox}>
            <Box className={classes.firstHeading}>A gift that delivers</Box>
            <Box className={classes.secondHeading}>
              Shoreline Menus gift cards for food lovers
            </Box>
            <Box className={classes.thirdHeading}>
              Grab a customizable gift card for your loved ones and send them
              the gift of a good meal.
            </Box>
            <Box className={classes.giftButtons}>
              <NavLink to={routeNames.purchaseGift}>
                <Button
                  className={classes.innerButton}
                  data-test-id="purchaseGift"
                >
                  Purchase a Gift Card
                </Button>
              </NavLink>
            </Box>
          </Box>
        </Box>
        <Box className={classes.backgroundSection}>
          <Typography className={classes.orText}>or</Typography>
          <Box>
            <Box className={classes.cardLeft} />
            <Box className={classes.cardRight} />
            <Box className={classes.giftCardSmall} />
          </Box>
          <Box className={classes.giftCardParent}>
            <Box className={classes.giftCardAnimationDesktop}>
              <img src={giftCardAnimation} alt="giftCardAnimation" />
            </Box>
            <Box className={classes.giftCardAnimationMedium}>
              <img src={giftAnimationMedium} alt="giftCardAnimation" />
            </Box>
          </Box>
        </Box>
      </Box>
      <Box className={classes.redeemCardContainer}>
        <Box className={classes.redeemCardBox}>
          <Card className={classes.redeemCard}>
            <Box>
              <Typography className={classes.redeemTitle}>
                Redeem Gift Card
              </Typography>
              <Typography className={classes.redeemDescription}>
                Enter your gift card code to apply it to your account balance.
              </Typography>
            </Box>
            <Box>
              <Box className={classes.inputButtonRow}>
                <TextField
                  InputProps={{ className: classes.gcCodeField }}
                  data-test-id="redeem-gc-code"
                  placeholder="Enter your code..."
                  value={gcCodeString}
                  onChange={(e) => handleChangeGCCode(e?.target?.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleRedeem()}
                  disabled={!isLoggedIn}
                  className={classes.gcCodeInput}
                />
                <Button
                  variant="contained"
                  size="large"
                  disabled={!gcCodeString || !isLoggedIn}
                  onClick={handleRedeem}
                  data-test-id="redeem-gc-button"
                  className={classes.redeemButton}
                >
                  Redeem
                </Button>
              </Box>
            </Box>
            {!isLoggedIn && (
              <Box className={classes.signInBox}>
                <Typography className={classes.signInTitle}>
                  Sign in to redeem your gift card
                </Typography>
                <Typography className={classes.signInSubtitle}>
                  You&apos;ll need to log in to your account to add the gift
                  card balance. Don&apos;t have an account? You can create one
                  during the login process.
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleSignInClick}
                  data-test-id="sign-in-button"
                  className={classes.signInButton}
                >
                  Log In or Sign Up
                </Button>
              </Box>
            )}
          </Card>
        </Box>
      </Box>
      <Box className={classes.lowerBox}>
        <Box className={classes.innerLowerBox}>
          <Grid container spacing={2}>
            <Grid item lg={4} md={12}>
              <Box className={classes.lowerBoxImage}>
                <img src={send} alt="emailImg" />
              </Box>
              <Box className={classes.lowerBoxTitle}>Select your gift card</Box>
              <Box className={classes.lowerBoxText}>
                Choose the design to fit the occasion, enter the amount of the
                gift, and add your own personal message.
              </Box>
            </Grid>
            <Grid item lg={3} md={12}>
              <Box className={classes.lowerBoxImage}>
                <img src={emailImg} alt="send" />
              </Box>
              <Box className={classes.lowerBoxTitle}>Send instantly</Box>
              <Box className={classes.lowerBoxText}>
                Last minute gift? The recipient can receive (and use) your gift
                in just minutes after you purchase your Gift Certificate. You
                can also schedule the Gift Certificate to be sent on the day of
                your choosing, like a birthday or anniversary.
              </Box>
            </Grid>
            <Grid item lg={4} md={12}>
              <Box className={classes.lowerBoxImage}>
                <img src={graph} alt="graph" />
              </Box>
              <Box className={classes.lowerBoxTitle}>
                Meals from many restaurants
              </Box>
              <Box className={classes.lowerBoxText}>
                Gift Certificates can be redeemed for Takeout and Delivery
                orders from any of the restaurants available on this site.
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Box>
      <Box mt={20}>
        <Box className={classes.questionParent}>
          <Box className={classes.questionBox}>
            <Box>
              <Box className={classes.questionMainHeading}>
                Frequently asked questions
              </Box>
              <Box className={classes.questionSubHeading}>
                Everything you need to know about the product and billing.
              </Box>
              <Box>
                <GiftAccordian />
              </Box>
            </Box>
          </Box>
        </Box>
        <Box className={classes.questionParent} mt={5} mb={10}>
          <Box className={classes.lastBoxParent}>
            <Box className={classes.questionParent}>
              <Box className={classes.subParentLastBox}>
                <Typography className={classes.stillQuestion}>
                  Still have questions?
                </Typography>
                <Typography>
                  Can’t find the answer you’re looking for? Please chat to our
                  friendly team. Email us
                </Typography>
                <Box className={classes.emailButton}>
                  <Button
                    className={classes.emailInnerButton}
                    data-test-id="emailUs"
                  >
                    Email us
                  </Button>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
      <Dialog open={isSignInOpen}>
        {signIn ? (
          <SignInForm
            onClose={toggleSignInModal}
            forgotPassword={() => setSignIn(false)}
          />
        ) : (
          <ForgotPassword
            onClose={toggleSignInModal}
            backToSignIn={() => setSignIn(true)}
          />
        )}
      </Dialog>
      <Dialog open={isSignUpOpen}>
        <SignUpForm onClose={toggleSignUpModal} />
      </Dialog>
    </Box>
  );
}

export default GiftCards;
